import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Toast } from '@capacitor/toast';
import { AlertController, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import write_blob from 'capacitor-blob-writer';
import { saveAs } from 'file-saver';

@Injectable({
  providedIn: 'root'
})
export class FileSystemService {

  constructor(
    private toastController: ToastController,
    private alertController: AlertController,
    private translate: TranslateService,
  ) { }

  async download(url: string|Blob, name?: string) {
    if (Capacitor.isNativePlatform()) {
      if (typeof url === 'string') {
        await this.downloadFile(url, name);
      } else {
        if (!name) {
          let filename = await this.askForFilename();
          if (filename) {
            name = filename;
          } else {
            return;
          }
        }
        await this.downloadBlobAsFile(url, name);
      }
    } else {
      await Toast.show({text: 'Download started!', duration: 'short', position: 'bottom'});
      saveAs(url, name);
    }
  }

  async askForFilename() {
    const alertInput = await this.alertController.create({
      header: this.translate.instant('save_as'),
      backdropDismiss: false,
      inputs: [{
        name: 'filename',
        type: 'text'
      }],
      buttons: [
        {
          text: this.translate.instant('cancel'),
          role: 'cancel'
        },
        {
          text: this.translate.instant('confirm'),
          role: 'confirm',
          handler: async (data) => {
            if(!data.filename){
              return false;
            }
          }
        }
      ]
    });

    await alertInput.present();
    const { data, role } = await alertInput.onDidDismiss();
    
    if (role === 'cancel') {
      return false;
    }

    return data?.values?.filename as string;
  }

  async downloadFile(url: string, name?: string) {
    const permissionStatus = await this.requestFilePermissions();
    
    if (permissionStatus === 'granted') {
      await Toast.show({text: 'Download started!', duration: 'short', position: 'bottom'});
      const result = await Filesystem.downloadFile({
          path: 'Avance/' + (name || this.getFileName(url)),
          url,
          directory: Directory.Documents,
          recursive: true
        });

      if (result) {
        alert(JSON.stringify(result));
        await this.showToast(this.translate.instant('entity_saved', {entity: '[' + result.path + ']' }));
      }
      
      return result.path;
    } else {
      this.showToast(this.translate.instant('permissions.file_permission'), 'danger');
      return false;
    }
  }

  async downloadBlobAsFile(blob: Blob, name: string) {
    const permissionStatus = await this.requestFilePermissions();
    
    if (permissionStatus === 'granted') {
      await Toast.show({text: 'Download started!', duration: 'short', position: 'bottom'});
      const result = await write_blob({
          path: 'Avance/' + name,
          directory: Directory.Documents,
          blob,
          recursive: true
        });

      if (result) {
        this.showToast(this.translate.instant('entity_saved', {entity: '[' + Directory.Documents + '/Avance/' + name + ']' }));
      }
      
      return result;
    } else {
      this.showToast(this.translate.instant('permissions.file_permission'), 'danger');
      return false;
    }
  }

  async requestFilePermissions() {
    const permissionStatus = await Filesystem.checkPermissions();
    if (permissionStatus.publicStorage != 'granted' && permissionStatus.publicStorage != 'denied') {
      const permissionStatus = await Filesystem.requestPermissions();
      return permissionStatus.publicStorage;
    }
    return permissionStatus.publicStorage;
  }

  getFileName(url: string) {
    let filename = url.lastIndexOf('/') ? url.substring(url.lastIndexOf('/') + 1) : url;
    return filename.split('-').pop();
  }

  async showToast(message: string, color: string = 'success') {
    const toast = await this.toastController.create({
      message,
      color,
      duration: 30000,
      buttons: [
        this.translate.instant('ok')
      ]
    });
    await toast.present();
  }

}
